@include host-component-properties;

:host {
  --paf-journey-summary-title-font-size-xl: 40;
  --paf-journey-summary-title-font-size-large: 32;
  --paf-journey-summary-title-font-size-medium: 28;
  --paf-journey-summary-title-font-size-small: 24;
  --paf-journey-summary-title-line-height-xl: 36;
  --paf-journey-summary-title-line-height-large: 48;
  --paf-journey-summary-title-line-height-medium: 42;
  --paf-journey-summary-title-line-height-small: 36;
  --paf-journey-summary-details-font-size-xl: 24;
  --paf-journey-summary-details-font-size-small: 20;
  --paf-journey-summary-details-line-height-xl: 36;
  --paf-journey-summary-details-line-height-small: 30;
  --paf-journey-summary-details-margin-block: 16;
  --paf-journey-summary-details-extra-spacing: 2;
  --paf-journey-summary-icon-size-large: 48;
  --paf-journey-summary-icon-size-medium: 32;
  --paf-journey-summary-icon-size-small: 24;
  --paf-journey-summary-icon-margin-inline: 24;
  --paf-journey-summary-icon-margin-block-small: 8;
  --paf-journey-summary-icon-color: var(--paf-global-post-brand-black);
  --paf-journey-summary-button-change-padding: 10;
  --paf-journey-summary-transition-duration: 300ms;
}

.summary {
  transition: opacity ease-in var(--paf-journey-summary-transition-duration);
  opacity: 0;

  .assistive-text {
    @include screen-reader-only;
  }

  &.loading .title-wrapper {
    opacity: 0;
  }

  .title {
    position: relative;

    .loader {
      @include skeleton-loading;

      right: 25%;
      left: 25%;
    }

    .title-wrapper {
      font-size: px-to-rem(var(--paf-journey-summary-title-font-size-small));
      line-height: px-to-rem(var(--paf-journey-summary-title-line-height-small));
      font-weight: var(--paf-global-font-weight-bold);
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      gap: px-to-rem(var(--paf-journey-summary-icon-margin-block-small));
      margin: 0;
      text-align: center;
      scroll-margin-block-start: px-to-rem(var(--paf-global-spacing-fixed-4x));
      outline: 0;

      &:focus {
        outline: 0;
      }

      @include mq($from: small) {
        font-size: px-to-rem(var(--paf-journey-summary-title-font-size-medium));
        line-height: px-to-rem(var(--paf-journey-summary-title-line-height-medium));
        flex-direction: row;
        gap: px-to-rem(var(--paf-journey-summary-icon-margin-inline));
      }

      @include mq($from: medium) {
        font-size: px-to-rem(var(--paf-journey-summary-title-font-size-large));
        line-height: px-to-rem(var(--paf-journey-summary-title-line-height-large));
      }

      @include mq($from: large) {
        font-size: px-to-rem(var(--paf-journey-summary-title-font-size-xl));
        line-height: px-to-rem(var(--paf-journey-summary-title-line-height-xl));
      }

      .icon {
        width: px-to-rem(var(--paf-journey-summary-icon-size-small));
        height: px-to-rem(var(--paf-journey-summary-icon-size-small));
        transform: rotateZ(90deg);
        fill: var(--paf-journey-summary-icon-color);

        @include mq($from: small) {
          width: px-to-rem(var(--paf-journey-summary-icon-size-medium));
          height: px-to-rem(var(--paf-journey-summary-icon-size-medium));
          transform: rotateZ(0);
        }

        @include mq($from: large) {
          width: px-to-rem(var(--paf-journey-summary-icon-size-large));
          height: px-to-rem(var(--paf-journey-summary-icon-size-large));
        }

        @include forced-colors {
          fill: CanvasText;
        }
      }
    }
  }

  .details {
    position: relative;

    &.loading .details-wrapper {
      opacity: 0;
    }

    .loader {
      @include skeleton-loading;

      right: 25%;
      left: 25%;
    }

    .details-wrapper {
      font-size: px-to-rem(var(--paf-journey-summary-details-font-size-small));
      line-height: px-to-rem(var(--paf-journey-summary-details-line-height-small));
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      justify-content: center;
      align-items: center;
      margin-inline: 0;
      margin-block: px-to-rem(var(--paf-journey-summary-details-margin-block)) 0;
      text-align: center;

      @include mq($from: small) {
        flex-direction: row;
      }

      @include mq($from: large) {
        font-size: px-to-rem(var(--paf-journey-summary-details-font-size-xl));
        line-height: px-to-rem(var(--paf-journey-summary-details-line-height-xl));
      }

      .details-hidden {
        @include screen-reader-only;
      }

      .details-visible {
        display: flex;
        flex-direction: column;

        @include mq($to: small) {
          margin-block-end: px-to-rem(var(--paf-journey-summary-details-extra-spacing));
        }

        @include mq($from: small) {
          flex-direction: row;
        }
      }

      .via {
        white-space: pre-wrap;
        display: contents;
      }

      .details-time {
        @include mq($from: small) {
          &:before,
          &:after {
            padding: 10px;
            content: "|";
          }
        }
      }

      .details-change {
        @include button-reset;

        font-size: inherit;
        cursor: pointer;
        text-decoration: underline;
        padding-block: px-to-rem(var(--paf-journey-summary-button-change-padding));
        margin-block-start: calc(-1 * px-to-rem(var(--paf-journey-summary-button-change-padding)));
        color: var(--paf-global-post-brand-black);

        @include mq($from: small) {
          margin-block: calc(-1 / 2 * px-to-rem(var(--paf-journey-summary-button-change-padding)));
        }

        @include hover-style {
          color: var(--paf-global-post-brand-secondary);

          @include forced-colors {
            color: LinkText;
          }
        }
      }
    }
  }
}

.summary.visible {
  opacity: 1;
}
